export type SequenceDelayUnit = "days" | "hours";

export const SEQUENCE_DELAY_LIMITS: Record<
  SequenceDelayUnit,
  { min: number; max: number; label: string }
> = {
  days: { min: 1, max: 20, label: "days" },
  hours: { min: 3, max: 480, label: "hours" },
};

export const SEQUENCE_DELAY_UNIT_OPTIONS = [
  { value: "days", label: "Days" },
  { value: "hours", label: "Hours" },
] as const;

export const normalizeSequenceDelayUnit = (unit: unknown): SequenceDelayUnit =>
  unit === "hours" ? "hours" : "days";

export const clampSequenceDelay = (value: number, unit: SequenceDelayUnit) => {
  const limits = SEQUENCE_DELAY_LIMITS[unit];
  if (!Number.isFinite(value)) return limits.min;
  return Math.max(limits.min, Math.min(limits.max, Math.floor(value)));
};

export const formatSequenceDelayLabel = (
  value: number,
  unit: SequenceDelayUnit = "days"
) => {
  if (value <= 0) return "No Delay";
  if (unit === "hours") return value === 1 ? "1 Hour" : `${value} Hours`;
  return value === 1 ? "1 Day" : `${value} Days`;
};

export const getSequenceDelayValidationError = (
  rawValue: string,
  unit: SequenceDelayUnit,
  options: { allowZero?: boolean } = {}
) => {
  const { allowZero = false } = options;
  const limits = SEQUENCE_DELAY_LIMITS[unit];
  if (rawValue.trim() === "") return "Delay is required.";
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return "Enter a valid number.";
  if (!Number.isInteger(parsed)) return "Delay must be a whole number.";
  if (allowZero && parsed === 0) return "";
  if (parsed < limits.min) return `Minimum delay is ${limits.min} ${limits.label}.`;
  if (parsed > limits.max) return `Maximum delay is ${limits.max} ${limits.label}.`;
  return "";
};
